"use client";

import CloseIcon from "@mui/icons-material/Close";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import { useTheme } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import countries from "i18n-iso-countries";
import { observer } from "mobx-react-lite";
import dynamic from "next/dynamic";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { makeTmdbImageUrl } from "@/shared/lib/make-tmdb-image-url.js";
import { useOnScreen } from "@/shared/lib/on-screen/use-on-screen.js";
import { getRatingColor } from "@/shared/lib/rating/get-rating-color.js";
import { useStore } from "@/shared/lib/store/use-store.js";
import { YandexAdBanner } from "@/widgets/ad-banner/yandex-ad-banner.js";
import { ViewBoxAppBar } from "@/widgets/app-bar/ui/app-bar.js";
import { ViewBoxAppBarBackButton } from "@/widgets/app-bar/ui/app-bar-back-button.js";
import { ViewBoxAppBarShareButton } from "@/widgets/app-bar/ui/app-bar-share-button.js";
import { ViewBoxAppBarTitle } from "@/widgets/app-bar/ui/app-bar-title.js";
import { ShowBlocked } from "@/widgets/show/ui/show-blocked.js";
import { ShowExternalPlayer } from "@/widgets/show/ui/show-external-player.js";
import { ShowOverview } from "@/widgets/show/ui/show-overview.js";

import { useTVSeriesPagePreloadData } from "../lib/use-tv-series-page-preload-data";

const ShowTrailer = dynamic(() =>
  import("@/widgets/show/ui/show-trailer.js").then((m) => ({
    default: m.ShowTrailer,
  })),
);

const RelatedSeries = dynamic(() =>
  import("@/features/tv/related-tv-series/ui/related-series.js").then((m) => ({
    default: m.RelatedSeries,
  })),
);

export const TVSeriesPage = observer(function TVSeriesPage({
  id,
}: {
  id: string;
}) {
  const {
    watchHistory,
    tvSeriesPage: { tmdbData: data, isLoading, isError, getData, resetData },
  } = useStore();
  const preloadedData = useTVSeriesPagePreloadData();
  const { formatMessage, locale } = useIntl();
  const theme = useTheme();
  const [titleRef, titleOnScreen] = useOnScreen<HTMLHeadingElement>();

  useEffect(() => {
    getData(Number(id), locale, preloadedData);

    return () => {
      resetData();
    };
  }, [id, locale, preloadedData, getData, resetData]);

  const title = data?.name ?? data?.original_name;

  const overview = data?.overview;

  const backdropPath = data?.backdrop_path;

  const imdbId = data?.external_ids?.imdb_id ?? null;

  const rating = Number(data?.vote_average).toFixed(1);

  const [trailerOpen, setTrailerOpen] = useState(false);
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const isBlocked = data && "blocked" in data;

  const years = useMemo(() => {
    if (isBlocked) {
      return null;
    }

    const firstEpisodeYear = data?.first_air_date
      ? new Date(data?.first_air_date).getFullYear()
      : null;
    const lastEpisodeYear = data?.last_air_date
      ? new Date(data?.last_air_date).getFullYear()
      : null;

    if (!firstEpisodeYear && !lastEpisodeYear) return null;

    let years = `${firstEpisodeYear}-${lastEpisodeYear}`;

    if (lastEpisodeYear === firstEpisodeYear || !lastEpisodeYear) {
      if (!firstEpisodeYear) return null;
      years = `${firstEpisodeYear}`;
    }

    return years;
  }, [data, isBlocked]);

  const genres = useMemo(() => {
    if (isBlocked) {
      return [];
    }

    return data?.genres
      .map((genre) => {
        const splitedGenre = genre.name.split(" ");

        if (splitedGenre.length === 3) {
          return splitedGenre[2];
        }

        return splitedGenre[0];
      })
      .slice(0, 2)
      .join(", ");
  }, [data, isBlocked]);

  const youtubeVideoId = useMemo(() => {
    if (isBlocked) return null;

    return (data?.videos?.results || []).filter(
      ({ site, type }) => site === "YouTube" && type === "Trailer",
    )[0]?.key;
  }, [data, isBlocked]);

  const handleToggleTrailerDialog = useCallback(
    (show = false) =>
      () => {
        setTrailerOpen(show);
      },
    [],
  );

  const handleIframeClick = useCallback(() => {
    if (!title || !backdropPath) {
      return;
    }

    watchHistory.addRecord({
      id: Number(id),
      title: title!,
      tmdbMediaType: "tv",
      tmdbBackdropPath: backdropPath,
      createdAt: new Date().toISOString(),
    });
  }, [id, title, backdropPath, watchHistory]);

  if (isError) {
    return (
      <Typography variant="body1">
        {formatMessage({ id: "unknown_error" })}
      </Typography>
    );
  }

  if (isBlocked) {
    return <ShowBlocked />;
  }

  return (
    <>
      <Dialog
        open={trailerOpen}
        fullScreen={fullScreen}
        onClose={handleToggleTrailerDialog(false)}
      >
        <Box minWidth={640} minHeight={480}>
          <AppBar
            sx={{
              [theme.breakpoints.up("sm")]: {
                visibility: "hidden",
              },
            }}
          >
            <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="subtitle1" fontWeight={"500"}>
                Трейлер
              </Typography>
              <IconButton edge="end" onClick={handleToggleTrailerDialog(false)}>
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>
          <Box paddingTop="20px" height="440" width="100%">
            {youtubeVideoId && (
              <ShowTrailer videoId={youtubeVideoId} embedded={false} />
            )}
          </Box>
        </Box>
      </Dialog>

      <ViewBoxAppBar
        primaryAction={<ViewBoxAppBarBackButton />}
        title={
          !titleOnScreen && <ViewBoxAppBarTitle>{title}</ViewBoxAppBarTitle>
        }
        actions={
          <ViewBoxAppBarShareButton id={Number(id)} tmdbMediaType="tv" />
        }
      />

      <Container
        maxWidth="lg"
        disableGutters
        sx={{
          [theme.breakpoints.up("lg")]: {
            px: 3,
          },
        }}
      >
        <YandexAdBanner
          blockId="R-A-9393300-3"
          renderTo="yandex_rtb_R-A-9393300-3"
        />
      </Container>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <Container
          maxWidth="lg"
          disableGutters
          sx={{
            [theme.breakpoints.up("lg")]: {
              px: 3,
            },
            overflow: "hidden",
            minHeight: "30.8rem",
            position: "relative",
          }}
        >
          <Box position={"relative"}>
            <Box
              sx={{
                zIndex: 1,
                position: "relative",
                padding: 10,
                [theme.breakpoints.down("md")]: {
                  padding: 6,
                },
                [theme.breakpoints.down("sm")]: {
                  padding: 2,
                  paddingRight: 4,
                  paddingLeft: 4,
                },
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }} mb={1}>
                <Box mb={2}>
                  {isLoading && (
                    <>
                      <Typography variant="h5" component="h2">
                        <Skeleton />
                      </Typography>
                      {/* Rating */}
                      <Skeleton variant="rectangular" width={240} height={24} />
                    </>
                  )}
                  {!isLoading && title ? (
                    <Typography
                      variant="h4"
                      fontWeight={"800"}
                      component="h2"
                      width={300}
                      ref={titleRef}
                    >
                      {title}
                    </Typography>
                  ) : null}
                  {data?.tagline && (
                    <Typography variant="subtitle1" color="grey.500">
                      {data.tagline}
                    </Typography>
                  )}
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                  {data?.vote_average && (
                    <Typography
                      variant="subtitle1"
                      color={getRatingColor(+rating)}
                      fontWeight={"500"}
                      mr={0.5}
                      sx={{
                        [theme.breakpoints.down("md")]: {
                          fontSize: "0.95rem",
                          lineHeight: 1,
                        },
                      }}
                    >
                      {rating}
                    </Typography>
                  )}

                  {years && (
                    <Typography
                      variant="subtitle1"
                      color="grey.500"
                      sx={{
                        [theme.breakpoints.down("md")]: {
                          fontSize: "0.95rem",
                          lineHeight: 1.1,
                        },
                      }}
                    >
                      {years},
                    </Typography>
                  )}

                  <Typography
                    variant="subtitle1"
                    color="grey.500"
                    mr={0.5}
                    sx={{
                      [theme.breakpoints.down("md")]: {
                        fontSize: "0.95rem",
                        lineHeight: 1.1,
                      },
                    }}
                  >
                    {genres}
                  </Typography>

                  {data?.production_countries?.[0]?.iso_3166_1 && (
                    <Typography
                      variant="subtitle1"
                      color="grey.500"
                      sx={{
                        [theme.breakpoints.down("md")]: {
                          fontSize: "0.95rem",
                          lineHeight: 1.1,
                        },
                      }}
                    >
                      {countries.getName(
                        data?.production_countries?.[0]?.iso_3166_1,
                        locale,
                      )}
                    </Typography>
                  )}

                  {data?.adult && (
                    <Typography
                      variant="subtitle1"
                      color="grey.500"
                      sx={{
                        [theme.breakpoints.down("md")]: {
                          fontSize: "0.95rem",
                          lineHeight: 1,
                        },
                      }}
                    >
                      18+
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  mb: 3,
                  gap: 1,
                }}
              >
                {isLoading && (
                  <>
                    <Typography variant="subtitle1">
                      <Skeleton />
                    </Typography>
                    <Typography variant="h6" component="h3">
                      <Skeleton />
                    </Typography>
                    <Typography variant="body1">
                      <Skeleton />
                    </Typography>
                  </>
                )}
                {overview && (
                  <>
                    <ShowOverview overview={overview} />
                  </>
                )}
              </Box>
              <Box>
                {youtubeVideoId && (
                  <Button
                    onClick={handleToggleTrailerDialog(true)}
                    variant="outlined"
                    color="secondary"
                    size="medium"
                  >
                    Трейлер
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              width: "100%",
              height: "100%",
              overflow: "hidden",
              position: "absolute",
              top: 0,
              left: 0,
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                backgroundImage: `url(${makeTmdbImageUrl("w1280", backdropPath)}), linear-gradient(to right, black, rgba(0, 0, 0, 0.2))`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                backgroundPosition: "100% 0",
                aspectRatio: "16 / 9",
                backgroundBlendMode: "color-burn",
              }}
            />
          </Box>
        </Container>

        {imdbId && (
          <ShowExternalPlayer
            tmdbMediaType="tv"
            tmdbId={Number(id)}
            imdbId={imdbId}
            onIframeClick={handleIframeClick}
          />
        )}

        {data?.id && (
          <Container sx={{ mb: 4 }}>
            <RelatedSeries seriesId={data?.id} />
          </Container>
        )}
      </Box>
    </>
  );
});
